<script setup lang="ts">
import { useFieldArray } from 'vee-validate'
import { computed, inject, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserSession } from '/@src/stores/userSession'
import * as apisPortal from '/@src/client-portal/api'
import { OsobaOrganizacieZaradenieZaradenieEnum } from '/@src/client-portal'
import { isEntityActive } from '/@src/composable/useSubjekt'

const emptyResponse = inject('emptyResponse')
const readOnlyResetKey = inject<string | number | undefined>('readOnlyResetKey')
const { t } = useI18n()
const userSession = useUserSession()
const isReadOnly = inject<boolean>('isReadOnly')
const getApi = new apisPortal.OrganizacieApi()
const kontakty = ref<any[]>([])
const vybranaOsoba = ref()
const { remove, push, fields } = useFieldArray('ziadatel.kontaktneOsoby')

onMounted(async () => {
  kontakty.value = !isReadOnly
    ? await getApi
        .portalGetOrganizaciaOsobaListOrganizacie(
          userSession?.user?.subjekt?.organizaciaId
        )
        .then((t) =>
          t.data?.filter(
            ({ zaradenia, meta }) =>
              isEntityActive({ meta }) &&
              zaradenia?.some(
                ({ zaradenie, meta }) =>
                  isEntityActive({ meta }) &&
                  zaradenie === OsobaOrganizacieZaradenieZaradenieEnum['KontaktnaOsoba']
              )
          )
        )
    : []
})

const osobySubjektuAutocomplete = computed(() => {
  return (
    kontakty.value
      ?.filter(
        (kontakt) =>
          !fields.value?.find(
            (field) => field.value?.osobaRef?.osobaId === kontakt?.osoba?.osobaId
          )
      )
      ?.map((osoba) => ({
        osobaId: osoba.osoba?.osobaId,
        menoUplne: osoba.osoba?.menoUplne,
      })) || []
  )
})

const getOsoba = (osobaId: string) => {
  const osobaKontext = kontakty.value.find((osoba) => osoba.osoba?.osobaId === osobaId)

  const osobaRef = {
    osobaId: osobaKontext?.osoba?.osobaId,
    menoUplne: osobaKontext?.osoba?.menoUplne,
    datumNarodenia: osobaKontext?.osoba?.datumNarodenia,
    adresaUplna: osobaKontext?.osoba?.adresaUplna,
  }

  return {
    osobaRef: osobaRef,
    kontakt: osobaKontext?.kontakt,
  }
}

const handleAdd = () => {
  if (vybranaOsoba.value) {
    push(getOsoba(vybranaOsoba.value.osobaId))
    vybranaOsoba.value = ''
  }
}
</script>

<template>
  <div v-if="emptyResponse.ziadatel.kontaktneOsoby || !isReadOnly">
    <div class="idsk-crossroad-line mb-8 mt-8"></div>

    <IDSKHeading as="h2" variant="l">
      {{ t('zoppm.step7.pravnickaOsoba.kontaktnaOsobaHeading') }}
    </IDSKHeading>
    <IDSKParagraph>
      {{ t('zoppm.step7.pravnickaOsoba.kontaktnaOsobaDescription.part1') }}
      <template v-if="isReadOnly">{{
        t('zoppm.step7.pravnickaOsoba.kontaktnaOsobaDescription.part2')
      }}</template>
      <router-link
        v-else
        v-slot="{ href, navigate }"
        to="/app/subjekt/organizacia/osoby"
        custom
      >
        <IDSKLink :href="href" @click="navigate">
          {{ t('zoppm.step7.pravnickaOsoba.kontaktnaOsobaDescription.part2') }}
        </IDSKLink>
      </router-link>
    </IDSKParagraph>

    <template v-if="!isReadOnly">
      <div v-for="(field, idx) in fields" :key="field.key" class="mb-5">
        <IDSKParagraph class="mb-1">
          <strong>{{ field.value.osobaRef.menoUplne }}</strong>
        </IDSKParagraph>
        <IDSKParagraph class="mb-1">
          {{ field.value.osobaRef.adresaUplna }}
        </IDSKParagraph>
        <IDSKParagraph class="mb-1">
          {{ field.value.kontakt.telefonneCislo }}
        </IDSKParagraph>
        <IDSKParagraph class="mb-4">
          {{ field.value.kontakt.email }}
        </IDSKParagraph>

        <IDSKLink class="mb-6" href="#" no-visited-state @click.prevent="remove(idx)">
          {{ t('zoppm.step7.pravnickaOsoba.odobratKontaktnaOsoba') }}
        </IDSKLink>
      </div>

      <div v-if="osobySubjektuAutocomplete?.length > 0" class="autocomplete--with-button">
        <IDSKAutocomplete
          id="kontaktna-osoba-autocomplete-po"
          :key="osobySubjektuAutocomplete?.length"
          v-model="vybranaOsoba"
          :placeholder="t('zoppm.step7.pravnickaOsoba.kontaktnaOsobaHint')"
          :label="t('zoppm.step7.pravnickaOsoba.vyhladajteUdajeZProfilu')"
          :options="osobySubjektuAutocomplete"
          option-object-value="osobaId"
          option-object-label="menoUplne"
        />
        <IDSKButton
          variant="secondary"
          :data-test-id="`zoppm-ziadatel-po-kontaktna-osoba-pridat`"
          @click="handleAdd"
        >
          {{ t('globalne.pridat') }}
        </IDSKButton>
      </div>
    </template>
    <template v-else>
      <div
        v-for="kontaktnaOsoba in emptyResponse.ziadatel.kontaktneOsoby"
        :key="kontaktnaOsoba"
        class="mb-5"
      >
        <IDSKParagraph class="mb-1">
          {{ t('zoppm.step7.fyzickaOsoba.menoAPriezviskoVzorova') }}
        </IDSKParagraph>
        <IDSKParagraph class="mb-1">
          {{ t('zoppm.step7.fyzickaOsoba.telefonneCislo') }}
        </IDSKParagraph>
        <IDSKParagraph class="mb-4">
          {{ t('zoppm.step7.pravnickaOsoba.osobaMuzEmail') }}
        </IDSKParagraph>

        <IDSKLink class="mb-6" href="javascript:void(0)" no-visited-state>
          {{ t('zoppm.step7.pravnickaOsoba.odobratKontaktnaOsoba') }}
        </IDSKLink>
      </div>

      <div class="autocomplete--with-button">
        <IDSKAutocomplete
          id="kontaktna-osoba-autocomplete-po-ro"
          :key="readOnlyResetKey"
          :placeholder="t('zoppm.step7.pravnickaOsoba.kontaktnaOsobaHint')"
          :label="t('zoppm.step7.pravnickaOsoba.vyhladajteUdajeZProfilu')"
          :options="[]"
        />
        <IDSKButton variant="secondary" :data-test-id="`zoppm-ziadatel-po-pridat`">
          {{ t('globalne.pridat') }}
        </IDSKButton>
      </div>
    </template>
  </div>
</template>

<style lang="scss"></style>
